/**
 * AI Spend by Feature (per-`surface` spend analytics)
 *
 * Answers the question the aggregate AI-usage view cannot: WHICH FEATURE spent
 * the money. Backed by `ion_llm_calls.surface` via
 * GET /admin/ai-usage/surface-spend — customer chat, the onboarding hero turn,
 * the summariser, cron briefings, usage reports, eval runs, cache warm-up, …
 *
 * Two conventions worth knowing before reading a number here:
 *
 * - **Eval traffic is INCLUDED by default** (`include_eval`) — with the toggle
 *   ON this total is computed over the same POPULATION as /summary and /by-user
 *   (DEC-4), which is what makes both comparable to the AWS bill. It is not the
 *   same NUMBER: the windows differ by construction — /summary is
 *   calendar-anchored (today / month-to-date) while this page is `range_days`
 *   back from now, and there is no control here that lines the two up. See
 *   admin/ai_usage/router.py::get_surface_spend. Switching the toggle OFF
 *   changes the population too: customer-driven spend only, which then sits
 *   BELOW /summary whenever an eval run happened in the window.
 * - **Every group ships its per-model rows.** Cost is tokens x per-model price
 *   (`backend/app/api/v1/chat/pricing.py`), so the model id next to the token
 *   counts is what lets you re-derive a total instead of trusting it. Expand a
 *   row to see it.
 *
 * Reporting only — nothing here touches quota or enforcement.
 */
import * as echarts from 'echarts';
import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getAiUsageSurfaceSpend } from '../../../services/adminService.js';
import { COLORS, chartTheme } from './shared/chartTheme.js';
import { formatNumber, formatUSD } from './shared/formatters.js';
import { KPICard } from './shared/KPICard.jsx';

// --------------------------------------------------------------------------
// Surface vocabulary (display only)
// --------------------------------------------------------------------------

// Mirrors `LLMSurface` in backend/app/api/v1/chat/llm/base.py. Display labels
// live here rather than server-side because they are UI copy; a surface added
// in the backend without a label falls back to its raw value (readable, not
// broken) — the response's `known_surfaces` is what tells us the vocabulary.
const SURFACE_LABELS = {
  customer_chat: 'Customer Chat',
  onboarding_hero: 'Onboarding Hero-Turn',
  summariser: 'Conversation Summariser',
  cache_warmup: 'Prompt-Cache Warm-up',
  feedback_briefing: 'Feedback-Briefing (entfernt, historisch)',
  report_rationale: 'Cell-Selection Rationale',
  usage_report_map: 'Usage Report (Digest)',
  usage_report_reduce: 'Usage Report (Final)',
  analytics_report: 'Business-Analytics Report',
  eval_run: 'Eval Run (Model under test)',
  eval_judge: 'Eval Judge (DeepEval)',
  token_count: 'Token Counting',
  smoketest: 'Bedrock Smoketest',
};

function surfaceLabel(surface) {
  return SURFACE_LABELS[surface] || surface.replace(/_/g, ' ');
}

// Stable-ish color per surface: index into the Batemo palette by position in
// the (cost-ordered) list, so the chart stack and the table read the same.
const SURFACE_PALETTE = [
  COLORS.orange,
  COLORS.cyan,
  COLORS.purple,
  COLORS.green,
  COLORS.orangeLight,
  COLORS.cyanLight,
  COLORS.red,
  COLORS.gray,
];

function surfaceColor(index) {
  return SURFACE_PALETTE[index % SURFACE_PALETTE.length];
}

function shortModel(modelId) {
  return (modelId || '').replace(/^(eu|global|us)\./, '');
}

// --------------------------------------------------------------------------
// KPI row
// --------------------------------------------------------------------------

function SpendKpis({ data }) {
  const top = data.surfaces[0];
  const customerShare = data.surfaces
    .filter((s) => data.ion_customer_surfaces.includes(s.surface))
    .reduce((acc, s) => acc + s.cost_share_pct, 0);

  return (
    <div className="analytics-kpi-grid">
      <KPICard
        icon={'💰'}
        label={`Spend (${data.range_days} Tage)`}
        value={formatUSD(data.total_cost_usd)}
        sublabel={`${formatNumber(data.total_request_count)} Requests · ${formatNumber(data.total_tokens)} Tokens`}
        color="orange"
      />
      <KPICard
        icon={'💬'}
        label="Customer Chat"
        value={formatUSD(data.ion_customer_cost_usd)}
        sublabel={`${customerShare.toFixed(1)}% des Spends`}
        color="orange"
      />
      <KPICard
        icon={'🏷️'}
        label="Teuerstes Feature"
        value={top ? surfaceLabel(top.surface) : '—'}
        sublabel={top ? `${formatUSD(top.cost_usd)} · ${top.cost_share_pct.toFixed(1)}%` : 'Keine Daten'}
        color="cyan"
      />
      <KPICard
        icon={'🧪'}
        label="Eval-Traffic"
        value={data.include_eval ? 'enthalten' : 'ausgeschlossen'}
        sublabel={
          data.include_eval
            ? 'Gleiche Grundmenge wie der AI-Usage-Tab'
            : 'Nur kundengetriebener Spend — liegt unter dem AI-Usage-Tab, wenn ein Eval-Run lief'
        }
        color={data.include_eval ? 'cyan' : 'orange'}
      />
    </div>
  );
}

// --------------------------------------------------------------------------
// Stacked spend-over-time chart
// --------------------------------------------------------------------------

// `metric` — 'cost' (USD) or 'tokens'. Series arrive index-aligned and
// zero-filled from the server (see `dense_surface_series`), so the stack can be
// built directly: no client-side bucket joining, hence no chance of shifting
// one surface's day onto another's.
function SurfaceStackChart({ timeseries, metric }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !timeseries?.buckets?.length) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const isHour = timeseries.bucket === 'hour';
    const labels = timeseries.buckets.map((b) =>
      isHour ? b.slice(5, 13).replace('T', ' ') : b.slice(0, 10)
    );
    const series = timeseries.series.map((s, i) => ({
      name: surfaceLabel(s.surface),
      type: 'bar',
      stack: 'spend',
      data:
        metric === 'cost'
          ? s.cost_usd.map((v) => parseFloat(v || '0'))
          : s.total_tokens,
      itemStyle: { color: surfaceColor(i) },
    }));

    chartInstance.current.setOption(
      {
        ...chartTheme,
        grid: { left: 70, right: 20, top: 40, bottom: 60 },
        tooltip: { ...chartTheme.tooltip, trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { ...chartTheme.legend, top: 0, type: 'scroll' },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: COLORS.grid } },
          axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 45 },
        },
        yAxis: {
          type: 'value',
          name: metric === 'cost' ? 'USD' : 'Tokens',
          axisLabel: {
            color: COLORS.gray,
            fontSize: 11,
            formatter: (v) => (metric === 'cost' ? '$' + v.toFixed(2) : formatNumber(v)),
          },
          splitLine: { lineStyle: { color: COLORS.grid } },
        },
        series,
      },
      { notMerge: true }
    );

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [timeseries, metric]);

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null; }, []);

  if (!timeseries?.buckets?.length) {
    return <div className="empty-state">Keine AI-Kosten im Zeitraum.</div>;
  }
  return <div ref={chartRef} style={{ width: '100%', height: 340 }} />;
}

// --------------------------------------------------------------------------
// Per-model detail rows (shared by both tables)
// --------------------------------------------------------------------------

// The verification layer: model id next to the token counts that produced the
// group's cost. Rendered as sub-rows of the group they belong to so the sum is
// checkable in place.
function ModelRows({ models, columns }) {
  if (!models || models.length === 0) {
    return (
      <tr>
        <td colSpan={columns} style={{ color: COLORS.gray, fontSize: 12, paddingLeft: 24 }}>
          Keine Modell-Details (keine Calls im Zeitraum).
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td colSpan={columns} style={{ padding: 0 }}>
        <table style={{ width: '100%', fontSize: 12, background: COLORS.background }}>
          <thead>
            <tr style={{ color: COLORS.gray }}>
              <th style={{ textAlign: 'left', paddingLeft: 24 }}>model_id</th>
              <th style={{ textAlign: 'right' }}>Requests</th>
              <th style={{ textAlign: 'right' }}>Input</th>
              <th style={{ textAlign: 'right' }}>Output</th>
              <th style={{ textAlign: 'right' }}>Cached</th>
              <th style={{ textAlign: 'right' }}>Cache Write</th>
              <th style={{ textAlign: 'right' }}>Kosten</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.model_id}>
                <td style={{ textAlign: 'left', paddingLeft: 24 }} title={m.model_id}>
                  {shortModel(m.model_id)}
                </td>
                <td style={{ textAlign: 'right' }}>{m.request_count}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(m.input_tokens)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(m.output_tokens)}</td>
                <td style={{ textAlign: 'right', color: COLORS.gray }}>
                  {formatNumber(m.cached_input_tokens)}
                </td>
                <td style={{ textAlign: 'right', color: COLORS.gray }}>
                  {formatNumber(m.cache_write_tokens)}
                </td>
                <td style={{ textAlign: 'right' }}>{formatUSD(m.cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

// --------------------------------------------------------------------------
// Surface breakdown table
// --------------------------------------------------------------------------

function SurfaceTable({ data }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  const spent = new Set(data.surfaces.map((s) => s.surface));
  // Features in the backend vocabulary that spent nothing in this window —
  // listed explicitly, because "no row" and "no spend" are different answers.
  const idle = data.known_surfaces.filter((s) => !spent.has(s));

  if (data.surfaces.length === 0) {
    return <div className="empty-state">Keine AI-Kosten im Zeitraum.</div>;
  }

  return (
    <div className="analytics-table-wrapper" style={{ overflowX: 'auto' }}>
      <table className="analytics-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Feature (surface)</th>
            <th>Anteil</th>
            <th>Kosten</th>
            <th>Requests</th>
            <th>Input</th>
            <th>Output</th>
            <th>Cached</th>
            <th>Cache Write</th>
            <th>Total Tokens</th>
          </tr>
        </thead>
        <tbody>
          {data.surfaces.map((s, i) => (
            <Fragment key={s.surface}>
              <tr
                onClick={() => toggle(s.surface)}
                style={{ cursor: 'pointer' }}
                title="Modelle ein-/ausklappen"
              >
                <td style={{ textAlign: 'left' }}>
                  <span style={{ color: surfaceColor(i), marginRight: 6 }}>
                    {expanded[s.surface] ? '▾' : '▸'}
                  </span>
                  {surfaceLabel(s.surface)}
                  <div style={{ color: COLORS.gray, fontSize: 11 }}>
                    {s.surface}
                    {!s.is_known_surface && (
                      <span
                        style={{ color: COLORS.red, marginLeft: 6 }}
                        title="Unbekannter surface-Wert — nicht in LLMSurface. Spend wird trotzdem gezählt."
                      >
                        ⚠ unbekannt
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>{s.cost_share_pct.toFixed(1)}%</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatUSD(s.cost_usd)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(s.request_count)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(s.input_tokens)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(s.output_tokens)}</td>
                <td style={{ textAlign: 'right', color: COLORS.gray }}>
                  {formatNumber(s.cached_input_tokens)}
                </td>
                <td style={{ textAlign: 'right', color: COLORS.gray }}>
                  {formatNumber(s.cache_write_tokens)}
                </td>
                <td style={{ textAlign: 'right' }}>{formatNumber(s.total_tokens)}</td>
              </tr>
              {expanded[s.surface] && <ModelRows models={s.models} columns={9} />}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td style={{ textAlign: 'left' }}>Summe</td>
            <td style={{ textAlign: 'right' }}>100%</td>
            <td style={{ textAlign: 'right' }}>{formatUSD(data.total_cost_usd)}</td>
            <td style={{ textAlign: 'right' }}>{formatNumber(data.total_request_count)}</td>
            <td colSpan={4} />
            <td style={{ textAlign: 'right' }}>{formatNumber(data.total_tokens)}</td>
          </tr>
        </tfoot>
      </table>
      {idle.length > 0 && (
        <p style={{ color: COLORS.gray, fontSize: 11, marginTop: 8 }}>
          Ohne Spend im Zeitraum: {idle.map(surfaceLabel).join(', ')}.
        </p>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Customer-chat role / segment breakdown
// --------------------------------------------------------------------------

function segmentColor(segment) {
  return segment === 'batemo' ? '#c9a53b' : COLORS.cyan;
}

function RoleTable({ data }) {
  const [expanded, setExpanded] = useState({});
  const rows = data.ion_customer_roles || [];

  if (rows.length === 0) {
    return <div className="empty-state">Keine Customer-Chat-Nutzung im Zeitraum.</div>;
  }

  const keyOf = (r) => `${r.admin_role || 'none'}|${r.segment}|${r.subscription_tier || 'none'}`;

  return (
    <div className="analytics-table-wrapper" style={{ overflowX: 'auto' }}>
      <table className="analytics-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Rolle</th>
            <th>Segment</th>
            <th>Subscription</th>
            <th>Nutzer</th>
            <th>Anteil</th>
            <th>Kosten</th>
            <th>Requests</th>
            <th>Input</th>
            <th>Output</th>
            <th>Cached</th>
            <th>Total Tokens</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = keyOf(r);
            return (
              <Fragment key={key}>
                <tr
                  onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                  style={{ cursor: 'pointer' }}
                  title="Modelle ein-/ausklappen"
                >
                  <td style={{ textAlign: 'left' }}>
                    <span style={{ color: COLORS.gray, marginRight: 6 }}>
                      {expanded[key] ? '▾' : '▸'}
                    </span>
                    {r.admin_role ? (
                      <span style={{ color: COLORS.cyan }}>{r.admin_role}</span>
                    ) : (
                      'keine Admin-Rolle'
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ color: segmentColor(r.segment), fontWeight: 600, fontSize: 11 }}>
                      {r.segment.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>{r.subscription_tier || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.user_count}</td>
                  <td style={{ textAlign: 'right' }}>{r.cost_share_pct.toFixed(1)}%</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatUSD(r.cost_usd)}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(r.request_count)}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(r.input_tokens)}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(r.output_tokens)}</td>
                  <td style={{ textAlign: 'right', color: COLORS.gray }}>
                    {formatNumber(r.cached_input_tokens)}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(r.total_tokens)}</td>
                </tr>
                {expanded[key] && <ModelRows models={r.models} columns={11} />}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <p style={{ color: COLORS.gray, fontSize: 11, marginTop: 8 }}>
        Nur die kundenseitigen Surfaces ({data.ion_customer_surfaces.join(', ')}) — Hintergrund-Jobs
        und Eval-Runs sind hier per Definition nicht enthalten, ihre Kosten stehen in der Tabelle
        oben. Anteile beziehen sich auf den Customer-Chat-Spend ({formatUSD(data.ion_customer_cost_usd)}),
        nicht auf den Gesamt-Spend. <strong>Segment</strong> = Batemo-Mailadresse vs. Kunde (dieselbe
        Domain-Liste, die auch die Quota-Ebene benutzt), <strong>nicht</strong> das Quota-Profil des
        Nutzers — das steht pro Nutzer in der AI-Usage-Tabelle.
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------
// Main view
// --------------------------------------------------------------------------

export function AnalyticsAiSurfaceSpendView({ periodDays = 30 }) {
  const [data, setData] = useState(null);
  const [includeEval, setIncludeEval] = useState(true);
  const [metric, setMetric] = useState('cost');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Same bucket convention as the AI Usage view / the backend's
  // range_days+bucket pair: hourly only for very short windows.
  const bucket = periodDays <= 2 ? 'hour' : 'day';

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAiUsageSurfaceSpend({ rangeDays: periodDays, bucket, includeEval }));
    } catch (err) {
      console.error('AI surface spend fetch error:', err);
      setError('Verbindungsfehler beim Laden der Feature-Kosten.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-line */ }, [periodDays, includeEval]);

  if (error) {
    return (
      <div className="analytics-error">
        <p>{error}</p>
        <button onClick={fetchData}>Erneut versuchen</button>
      </div>
    );
  }
  if (!data) return <div className="analytics-loading"><div className="spinner" /></div>;

  return (
    <div className="ai-surface-spend-view" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h3 className="section-title">Kosten pro Feature</h3>
        <p style={{ color: COLORS.gray, fontSize: 11, marginTop: -6, marginBottom: 12 }}>
          Quelle: <code>ion_llm_calls.surface</code> aus der Datenbank dieser Umgebung (kein
          Production-Redirect). Kosten sind berechnet (Tokens × Modellpreis), nicht von AWS
          abgerechnet — die AWS-Gegenprobe steht im AI-Usage-Tab. Zeile aufklappen zeigt die
          <code>model_id</code>-Details, mit denen sich jede Summe nachrechnen lässt.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: COLORS.gray, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={includeEval}
            onChange={(e) => setIncludeEval(e.target.checked)}
          />
          Eval-Traffic einrechnen
        </label>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={() => setMetric((m) => (m === 'cost' ? 'tokens' : 'cost'))}
          title="Chart-Metrik umschalten"
        >
          {metric === 'cost' ? 'Chart: Tokens zeigen' : 'Chart: USD zeigen'}
        </button>
        {loading && <span className="spinner-small" />}
      </div>

      <SpendKpis data={data} />

      <section>
        <h3 className="section-title">
          Spend im Zeitverlauf {metric === 'cost' ? '(USD)' : '(Tokens)'} — gestapelt pro Feature
        </h3>
        <SurfaceStackChart timeseries={data.timeseries} metric={metric} />
      </section>

      <section>
        <h3 className="section-title">Feature-Breakdown</h3>
        <SurfaceTable data={data} />
      </section>

      <section>
        <h3 className="section-title">Customer Chat — nach Rolle und Segment</h3>
        <RoleTable data={data} />
      </section>
    </div>
  );
}
